import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { moneyLocale, parseMoneyInput, toEditText } from '../lib/money'

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  /** Decimal string ("1234.50") or null for an empty field. */
  value: string | null
  /** The parsed value (the last valid one while the text is invalid) and whether the text is valid. */
  onChange: (value: string | null, valid: boolean) => void
  allowNegative?: boolean
}

/** Free text in ("1 234,50", "1234.5"), decimal string out (spec §10). Keeps what the person typed while editing. */
const MoneyInput = React.forwardRef<HTMLInputElement, Props>(({ value, onChange, allowNegative, className, ...rest }, ref) => {
  const { i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const [text, setText] = useState(() => (value === null ? '' : toEditText(value, locale)))
  const [invalid, setInvalid] = useState(false)

  useEffect(() => {
    // Our own onChange echoes back as `value`: the text already says that, keep it as typed.
    // Anything else (a dialog reset) replaces the text.
    const parsed = parseMoneyInput(text, { allowNegative })
    if (parsed.ok && parsed.value === value) return
    setText(value === null ? '' : toEditText(value, locale))
    setInvalid(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <Input
      ref={ref}
      inputMode="decimal"
      value={text}
      aria-invalid={invalid || undefined}
      className={cn('text-right tabular-nums', invalid && 'border-destructive focus-visible:ring-destructive', className)}
      onChange={(e) => {
        const next = e.target.value
        setText(next)
        const parsed = parseMoneyInput(next, { allowNegative })
        setInvalid(!parsed.ok)
        onChange(parsed.ok ? parsed.value : value, parsed.ok)
      }}
      {...rest}
    />
  )
})
MoneyInput.displayName = 'MoneyInput'
export default MoneyInput
